import React, { useState, useEffect } from 'react';
import {
  Calendar, Clock, AlertCircle, CheckCircle2, Sparkles, Zap, Star,
  Inbox, ListTodo, ChevronDown, ChevronRight,
} from 'lucide-react';
import {
  OrkaText, OrkaSurface, OrkaButton, OrkaBadge, OrkaDisclosure,
  OrkaStatePill, OrkaPriorityChip, OrkaIntentPill, OrkaLoader,
  OrkaTaskCard, OrkaTaskRow, OrkaTaskIdentityStrip,
  OrkaPane,
} from '../../components/orka';
import { PRIORITY_META, getPriority } from '../../design-system/priority';
import { TASK_STATE_META, getState } from '../../design-system/states';
import { INTENT_META } from '../../design-system/intents';
import { AGENT_TONES, toneVars } from '../../design-system/agentTones';
import { DEFAULT_EFFORT_SCALE } from '../../design-system/effortScale';
import { TASK_FIELD_GROUPS } from '../../design-system/taskFieldGroups';
import { DS_GROUPS, sectionsForGroup } from './sections';
import {
  DatePickerCalendar,
  DatePickerTriggerVariants,
  DatePickerCellMatrix,
  DatePickerGridSizes,
  DatePickerEffortLegend,
  DatePickerDecisionBar,
} from './DatePickerShowcase';
import AppLayoutsShowcase from '../../components/design-system/AppLayoutsShowcase';
import ColorSystemShowcase from '../../components/design-system/ColorSystemShowcase';
import OrkaTaskCardV5Preview from '../../components/design-system/OrkaTaskCardV5';
import {
  AppShellVariantsShowcase,
  LeftPanelAnatomyShowcase,
} from '../../components/design-system/AppShellShowcase';
import {
  DailyPanesScope,
  DailyPanesDesktopLayout,
  DailyPanesStates,
  DailyPanesMobileLayout,
  DailyPanesImplementation,
} from './DailyPanesShowcase';

/* eslint-disable no-unused-vars */

/**
 * /owner/design-system — unified reference surface.
 *
 * Visual contract (apply to every section):
 *   - Sidebar tree sub-menu: drives anchor navigation & auto-expand.
 *   - Section shell (Collapsible): eyebrow + title + subtitle + demo slot.
 *   - Demo slot: always wrapped in <DSDemoFrame/> with optional row captions.
 *   - Task demos: always use the canonical <OrkaTaskCard/> (no bespoke cards).
 *
 * Restructured for V4:
 *   - Task identity group: Closed · Open w/ field groups · Variants ·
 *     Field groups registry · OrkaTaskRow + IdentityStrip.
 */

/* ————————————————————————— SHARED SHELLS ————————————————————————————————— */

function Collapsible({ id, title, subtitle, eyebrow, testId, defaultOpen = false, children, openSignal }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { if (openSignal === id) setOpen(true); }, [openSignal, id]);
  return (
    <section id={id} className="mb-4 scroll-mt-24" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border orka-border-subtle orka-surface-raised hover:orka-surface-selected orka-transition-colors"
        aria-expanded={open}
        data-testid={`${testId}-toggle`}
      >
        <div className="text-left min-w-0 flex-1">
          {eyebrow && <OrkaText size="micro" variant="info" className="block mb-0.5 font-bold tracking-wider uppercase">{eyebrow}</OrkaText>}
          <OrkaText as="div" size="small" variant="primary" className="font-semibold">{title}</OrkaText>
          {subtitle && <OrkaText size="micro" variant="tertiary" className="block mt-0.5 normal-case tracking-normal">{subtitle}</OrkaText>}
        </div>
        {open ? <ChevronDown className="w-4 h-4 shrink-0 orka-text-tertiary" /> : <ChevronRight className="w-4 h-4 shrink-0 orka-text-tertiary" />}
      </button>
      {open && (
        <OrkaSurface variant="raised" radius="xl" className="p-5 mt-2 border orka-border-subtle">
          {children}
        </OrkaSurface>
      )}
    </section>
  );
}

/** Unified demo frame — wraps every preview so sections feel identical. */
function DSDemoFrame({ caption, children, className = '' }) {
  return (
    <div className={`rounded-xl border orka-border-subtle orka-surface-base p-4 ${className}`}>
      {caption && <OrkaText size="micro" variant="tertiary" className="block mb-3 uppercase tracking-wider font-bold">{caption}</OrkaText>}
      <div>{children}</div>
    </div>
  );
}

function GroupHeader({ group }) {
  return (
    <header className="mt-10 mb-3 flex items-baseline justify-between" data-testid={`ds-group-header-${group.key}`}>
      <div>
        <OrkaText as="h2" size="h3" variant="primary">{group.label}</OrkaText>
        <OrkaText size="micro" variant="tertiary" className="normal-case tracking-normal">{group.hint}</OrkaText>
      </div>
      <a href={`#${group.key}-top`} className="text-[10px] uppercase tracking-wider orka-text-tertiary hover:orka-text-secondary">Jump</a>
    </header>
  );
}

/* ————————————————————————— PAGE ————————————————————————————————————————— */

export default function DesignSystemPreviewPage() {
  const [theme, setTheme] = useState(() =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light'
  );
  const [openSignal, setOpenSignal] = useState(() => (typeof window !== 'undefined' ? window.location.hash.replace('#', '') : ''));

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.classList.toggle('dark', next === 'dark');
    setTheme(next);
  };

  useEffect(() => {
    const onHash = () => {
      const id = window.location.hash.replace('#', '');
      if (!id) return;
      setOpenSignal(id);
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    };
    window.addEventListener('hashchange', onHash);
    if (window.location.hash) onHash();
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const renderers = useSectionRenderers(theme);

  return (
    <div className="orka-surface-page min-h-screen px-6 py-10" data-testid="design-system-preview">
      <div className="max-w-6xl mx-auto">
        <header className="flex items-start justify-between mb-8">
          <div>
            <OrkaText as="h1" size="h2" variant="primary" className="block mb-1">Orka V3/V4 Design System</OrkaText>
            <OrkaText size="body" variant="secondary">
              Unified reference. Every section follows the same shell — eyebrow, title, subtitle, demo frame. Task
              previews all use the canonical <code className="font-mono text-[12px] orka-text-primary">OrkaTaskCard</code>.
            </OrkaText>
          </div>
          <OrkaButton variant="secondary" size="sm" icon={theme === 'dark' ? Sparkles : Star} onClick={toggleTheme} data-testid="ds-theme-toggle">
            {theme === 'dark' ? 'Light' : 'Dark'} mode
          </OrkaButton>
        </header>

        {DS_GROUPS.map((group) => {
          const sections = sectionsForGroup(group.key);
          return (
            <div key={group.key} id={`${group.key}-top`}>
              <GroupHeader group={group} />
              {sections.map((s, idx) => (
                <Collapsible
                  key={s.id}
                  id={s.id}
                  eyebrow={`${String(idx + 1).padStart(2, '0')} / ${group.label}`}
                  title={s.label}
                  subtitle={renderers[s.id]?.subtitle}
                  testId={s.testId}
                  defaultOpen={s.defaultOpen}
                  openSignal={openSignal}
                >
                  {renderers[s.id]?.render() ?? (
                    <OrkaText size="small" variant="tertiary">No preview available for this section yet.</OrkaText>
                  )}
                </Collapsible>
              ))}
            </div>
          );
        })}

        <footer className="mt-12 pb-12 border-t pt-6 orka-border-subtle">
          <OrkaText size="small" variant="tertiary">
            V4 reconciliation slice shipped. Every task-identity preview renders via <code className="font-mono text-[12px]">OrkaTaskCard</code>
            {' '}with <code className="font-mono text-[12px]">fieldGroups</code> wired to the shared registry.
          </OrkaText>
        </footer>
      </div>
    </div>
  );
}

/* ————————————————————————— DEMO FIXTURES ————————————————————————————————— */

const DEMO_TASK = {
  task_id: 'T-4281',
  projectKey: 'billing',
  task_title: 'Review paid-tier billing gate flow',
  task_description: 'Check lock states, upgrade prompt, Stripe fallback behaviour, and ensure the free-to-paid pathway does not break the task flow.',
  task_next_steps: 'Confirm entitlement copy, then test the upgrade route in the dashboard and mobile pane.',
  related_epic_name: 'Freemium + Billing Launch',
  work_area_name: 'Billing / SaaS',
  task_intent: 'focus',
  task_status: 'In Progress',
  task_priority: 1,
  task_date_for_delivery: '2026-05-01',
  task_start_time: '09:00',
  task_end_time: '10:00',
  task_estimated_minutes: 60,
  task_estimated_effort: 3,
  task_actual_effort: 2,
  task_personal_effort: 0,
  is_meeting: true,
  imported_from_calendar: true,
  task_assignee: 'Joseph',
  task_observers: 'Roman, Denver',
  meeting_attendees: 'Joseph, Roman, Denver',
  google_meet_url: 'meet.google.com/ork-bill-gate',
  meeting_location: 'Google Meet',
  meeting_organizer: 'Joseph',
  task_recurrence: 'Weekly on Friday',
  task_recurrence_rule: 'RRULE:FREQ=WEEKLY;BYDAY=FR;UNTIL=20260630',
  task_recurrence_scope: 'This and following',
  task_reminders: '10 min popup, 1 day email',
  task_impact: 'High — affects conversion, upgrade trust, and premium launch quality.',
  task_blocked_reason: '',
  task_daily_sequence: 'A',
  task_scope: 'Work',
  task_hidden: false,
  task_reschedule_log: 'Moved from Apr 30 16:00 after design-system review overran.',
  created_by: 'Joseph',
  created_at: '2026-04-30 14:20',
  updated_at: '2026-04-30 17:35',
  task_source: 'Dashboard capture',
};

const DEMO_TASKS_VARIANTS = [
  { ...DEMO_TASK, task_id: 'T-V1', projectKey: 'billing',  task_title: 'Create paid-tier billing gate for Orka', task_priority: 1, task_status: 'In Progress', task_estimated_effort: 5 },
  { ...DEMO_TASK, task_id: 'T-V2', projectKey: 'identity', task_title: 'Refactor identity wipe and reinvite flow', task_priority: -1, task_status: 'Blocked', task_estimated_effort: 8 },
  { ...DEMO_TASK, task_id: 'T-V3', projectKey: 'growth',   task_title: 'Trial-to-paid conversion experiment', task_priority: 2, task_status: 'Partial', task_estimated_effort: 3 },
  { ...DEMO_TASK, task_id: 'T-V4', projectKey: 'ops',      task_title: 'Reconcile payroll cash-flow tasks', task_priority: 3, task_status: 'To Do', task_estimated_effort: 2 },
];

/* ————————————————————————— DATE PICKER DEMO (stateful) ————————————————— */

function DatePickerCalendarDemo() {
  const [mode, setMode] = useState('loaded');
  return (
    <>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <OrkaText size="micro" variant="tertiary" className="font-bold uppercase tracking-wider">Mode:</OrkaText>
        {['loaded', 'loading', 'stale', 'error', 'locked'].map((m) => (
          <OrkaButton
            key={m}
            variant={mode === m ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setMode(m)}
            data-testid={`ds-date-mode-${m}`}
          >
            {m}
          </OrkaButton>
        ))}
      </div>
      <DSDemoFrame caption="Calendar popover (Feb 2026 · today = 2026-02-14)">
        <div className="max-w-[420px]"><DatePickerCalendar year={2026} monthIndex={1} mode={mode} /></div>
      </DSDemoFrame>
      <div className="h-3" />
      <DSDemoFrame caption="Pinned-tooltip variant (mobile / touch)">
        <div className="max-w-[420px]"><DatePickerCalendar year={2026} monthIndex={1} mode={mode} pinnedTooltip /></div>
      </DSDemoFrame>
    </>
  );
}

/* ————————————————————————— SECTION RENDERERS ——————————————————————————— */

function useSectionRenderers(theme = 'light') {
  return {
    /* FOUNDATIONS */
    surfaces: {
      subtitle: '§6.1 — page / base / raised / subtle / selected / disabled',
      render: () => (
        <DSDemoFrame caption="Surface tokens">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {['page', 'base', 'raised', 'subtle', 'selected', 'disabled'].map(v => (
              <OrkaSurface key={v} variant={v} radius="lg" className="p-4 text-center">
                <OrkaText size="small" variant="secondary">{v}</OrkaText>
              </OrkaSurface>
            ))}
          </div>
        </DSDemoFrame>
      ),
    },
    text: {
      subtitle: '§6.2 — semantic text colours',
      render: () => (
        <DSDemoFrame caption="All text variants">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {['primary', 'secondary', 'tertiary', 'disabled', 'inverse', 'success', 'warning', 'danger', 'info', 'intent'].map(v => (
              <div key={v} className={v === 'inverse' ? 'bg-slate-900 dark:bg-slate-100 px-3 py-2 rounded-lg' : 'px-3 py-2'}>
                <OrkaText size="micro" variant={v}>{v}</OrkaText>
                <OrkaText size="body" variant={v} className="block">The quick fox</OrkaText>
              </div>
            ))}
          </div>
        </DSDemoFrame>
      ),
    },
    priority: {
      subtitle: '§6.5 — single source of truth for P-1 → P5',
      render: () => (
        <DSDemoFrame caption="Priority swatches">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.values(PRIORITY_META).map(p => (
              <div key={p.key} className="flex items-center gap-3">
                <span className="w-1.5 h-10 rounded-full shrink-0" style={{ backgroundColor: p.color }} aria-hidden="true" />
                <div>
                  <OrkaText size="small" variant="primary">{p.short}</OrkaText>
                  <OrkaText size="micro" variant="tertiary">{p.label}</OrkaText>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-[11px] orka-text-tertiary font-mono">
            <code>getPriority(-1)</code> → <code>{String(getPriority(-1).key)}</code>; <code>getPriority(2)</code> → <code>{String(getPriority(2).key)}</code>
          </div>
        </DSDemoFrame>
      ),
    },
    states: {
      subtitle: '§6.4 — replaces .status-* utilities and ad-hoc logic',
      render: () => (
        <DSDemoFrame caption="Task state badges">
          <div className="flex flex-wrap items-center gap-2">
            {Object.values(TASK_STATE_META).map(s => (
              <OrkaBadge key={s.key} tone={s.tone === 'recover' ? 'warning' : s.tone === 'muted' ? 'neutral' : s.tone}>
                {s.short}
              </OrkaBadge>
            ))}
          </div>
          <div className="mt-3 text-[11px] orka-text-tertiary font-mono">
            <code>getState('To Do').short</code> → <code>{getState('To Do').short}</code>; <code>getState('In Progress').short</code> → <code>{getState('In Progress').short}</code>
          </div>
        </DSDemoFrame>
      ),
    },
    intents: {
      subtitle: '§6.3 — what the component helps the user DO',
      render: () => (
        <DSDemoFrame caption="Intent vocabulary">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.values(INTENT_META).map(i => (
              <div key={i.key} className="flex items-center gap-2.5 px-3 py-2 rounded-lg orka-surface-subtle">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: i.color }} aria-hidden="true" />
                <OrkaText size="small" variant="primary">{i.label}</OrkaText>
              </div>
            ))}
          </div>
        </DSDemoFrame>
      ),
    },
    tones: {
      subtitle: '§6.6 — five-colour AI surface vocabulary',
      render: () => (
        <DSDemoFrame caption="Agent tones">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {Object.values(AGENT_TONES).map(t => (
              <div key={t.key} className="px-3 py-3 rounded-xl border" style={{ ...toneVars(t.key), backgroundColor: 'var(--tone-soft)', borderColor: 'var(--tone-border)' }}>
                <OrkaText size="micro" style={{ color: t.color }} as="div">{t.label}</OrkaText>
                <OrkaText size="small" variant="secondary" className="block mt-1">tone={t.key}</OrkaText>
              </div>
            ))}
          </div>
        </DSDemoFrame>
      ),
    },
    effort: {
      subtitle: '§6 — product canon for date picker + capacity rails',
      render: () => (
        <DSDemoFrame caption="Effort scale buckets">
          <div className="space-y-2">
            {DEFAULT_EFFORT_SCALE.map(b => (
              <div key={b.key} className="flex items-center gap-3 text-xs">
                <span className="w-3 h-3 rounded-full shrink-0 border" style={{ backgroundColor: b.color === 'transparent' ? 'transparent' : b.color, borderColor: b.color === 'transparent' ? 'var(--orka-border-default)' : b.color }} aria-hidden="true" />
                <span className="font-mono w-20 orka-text-tertiary">{b.min}–{b.max === 999 ? '∞' : b.max}</span>
                <OrkaText size="small" variant="primary">{b.label}</OrkaText>
                <OrkaText size="small" variant="tertiary" className="ml-auto">{b.meaning}</OrkaText>
              </div>
            ))}
          </div>
        </DSDemoFrame>
      ),
    },

    /* PRIMITIVES */
    buttons: {
      subtitle: 'primary / secondary / ghost / danger / ai · sizes · states',
      render: () => (
        <>
          <DSDemoFrame caption="Variants">
            <div className="flex flex-wrap items-center gap-3">
              <OrkaButton variant="primary" icon={Sparkles}>Primary</OrkaButton>
              <OrkaButton variant="secondary" icon={Calendar}>Secondary</OrkaButton>
              <OrkaButton variant="ghost" icon={Clock}>Ghost</OrkaButton>
              <OrkaButton variant="danger" icon={AlertCircle}>Danger</OrkaButton>
              <OrkaButton variant="ai" icon={Zap}>AI</OrkaButton>
            </div>
          </DSDemoFrame>
          <div className="h-3" />
          <DSDemoFrame caption="Sizes & states">
            <div className="flex flex-wrap items-center gap-3">
              <OrkaButton variant="secondary" size="sm">Small</OrkaButton>
              <OrkaButton variant="secondary" size="md">Medium</OrkaButton>
              <OrkaButton variant="secondary" size="lg">Large</OrkaButton>
              <OrkaButton variant="secondary" size="icon" icon={CheckCircle2}>Check</OrkaButton>
              <OrkaButton variant="primary" loading>Saving…</OrkaButton>
              <OrkaButton variant="primary" disabled>Disabled</OrkaButton>
            </div>
          </DSDemoFrame>
        </>
      ),
    },
    badges: {
      subtitle: 'tone variants · fill and outline',
      render: () => (
        <>
          <DSDemoFrame caption="Filled">
            <div className="flex flex-wrap items-center gap-2">
              {['neutral', 'info', 'success', 'warning', 'danger', 'intent'].map(tone => <OrkaBadge key={tone} tone={tone}>{tone}</OrkaBadge>)}
            </div>
          </DSDemoFrame>
          <div className="h-3" />
          <DSDemoFrame caption="Outline">
            <div className="flex flex-wrap items-center gap-2">
              {['neutral', 'info', 'success', 'warning', 'danger', 'intent'].map(tone => <OrkaBadge key={`o-${tone}`} tone={tone} outline>{tone} outline</OrkaBadge>)}
            </div>
          </DSDemoFrame>
        </>
      ),
    },
    disclosure: {
      subtitle: 'progressive disclosure for thinking / running / awaiting / done / failure',
      render: () => (
        <DSDemoFrame caption="OrkaDisclosure — 5 tones">
          <div className="space-y-3">
            <OrkaDisclosure title="Show thinking" tone="thinking" summary="3 reasoning steps">
              <ol className="text-xs space-y-1 orka-text-secondary list-decimal list-inside">
                <li>Inspected the active conversation context</li>
                <li>Resolved the Tasks capability via TEG v2</li>
                <li>Composed final response</li>
              </ol>
            </OrkaDisclosure>
            <OrkaDisclosure title="Tool execution" tone="running" summary="2 tools running" defaultOpen>
              <ul className="text-xs space-y-1 orka-text-secondary">
                <li>· orka.tasks.list — running…</li>
                <li>· orka.calendar.search — queued</li>
              </ul>
            </OrkaDisclosure>
            <OrkaDisclosure title="Awaiting your approval" tone="awaiting" summary="L3 — confirm">
              <OrkaText size="small" variant="secondary">This action will move 4 tasks to next week. Confirm?</OrkaText>
            </OrkaDisclosure>
            <OrkaDisclosure title="Action trail" tone="done" summary="3 done · undo available">
              <OrkaText size="small" variant="secondary">All scheduled tasks were committed. Undo expires in 8 minutes.</OrkaText>
            </OrkaDisclosure>
            <OrkaDisclosure title="Failure breakdown" tone="failure" summary="1 of 4 tools failed">
              <OrkaText size="small" variant="secondary">orka.gmail.send_email — quota exceeded. Retry in 60s.</OrkaText>
            </OrkaDisclosure>
          </div>
        </DSDemoFrame>
      ),
    },
    'surface-component': {
      subtitle: 'canonical container — base / raised / subtle',
      render: () => (
        <DSDemoFrame caption="OrkaSurface variants">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {['base', 'raised', 'subtle'].map(v => (
              <OrkaSurface key={v} variant={v} className="p-4">
                <OrkaText size="micro" variant="tertiary">VARIANT={v}</OrkaText>
                <OrkaText size="small" variant="primary" className="block mt-1">{v === 'base' ? 'Default container' : v === 'raised' ? 'Card / pane' : 'Inset / nested'}</OrkaText>
              </OrkaSurface>
            ))}
          </div>
        </DSDemoFrame>
      ),
    },
    'phase2-atoms': {
      subtitle: 'replaces duplicate priority/state/intent/loader implementations',
      render: () => (
        <>
          <DSDemoFrame caption="Priority chips">
            <div className="flex flex-wrap items-center gap-2">
              <OrkaPriorityChip priority="emergency" />
              <OrkaPriorityChip priority="fixed" />
              <OrkaPriorityChip priority="high" />
              <OrkaPriorityChip priority="medium" />
              <OrkaPriorityChip priority="low" />
              <OrkaPriorityChip priority={-1} />
              <OrkaPriorityChip priority={2} />
            </div>
          </DSDemoFrame>
          <div className="h-3" />
          <DSDemoFrame caption="State pills">
            <div className="flex flex-wrap items-center gap-2">
              {Object.values(TASK_STATE_META).map(s => <OrkaStatePill key={s.key} state={s.key} />)}
              <OrkaStatePill state="To Do" outline />
            </div>
          </DSDemoFrame>
          <div className="h-3" />
          <DSDemoFrame caption="Intent pills">
            <div className="flex flex-wrap items-center gap-2">
              {Object.keys(INTENT_META).slice(0, 6).map(k => <OrkaIntentPill key={k} intent={k} />)}
            </div>
          </DSDemoFrame>
          <div className="h-3" />
          <DSDemoFrame caption="Loaders">
            <div className="flex flex-wrap items-center gap-3">
              <OrkaLoader variant="spinner" tone="thinking" />
              <OrkaLoader variant="spinner" tone="running" />
              <OrkaLoader variant="dot" tone="awaiting" />
              <div className="w-32"><OrkaLoader variant="shimmer" tone="thinking" /></div>
            </div>
          </DSDemoFrame>
        </>
      ),
    },

    /* TASK IDENTITY — TWO families.
       (1) V5 (canonical) — single unified showcase with Base + 60-min
           spotlight + six effort tiers. Theme follows the page setting.
       (2) Field-Groups — DEPRECATED OrkaTaskCard kept for visual reference. */
    'task-card-v5': {
      subtitle: 'V5 · CANONICAL Task Card. One source of truth — width-responsive Base + 60-min day-planner spotlight + six effort tiers (15 / 30 / 60 / 180 / 360 / 600 min) all in one section. Light by default; flip to dark via the page Light/Dark toggle. Pencil + chevron live top-right of every header.',
      render: () => (
        <DSDemoFrame caption="V5 — Task Card · Base · 60-min · Effort tiers · light/dark via page toggle">
          <OrkaTaskCardV5Preview theme={theme} />
        </DSDemoFrame>
      ),
    },
    'task-card-closed': {
      subtitle: '⚠️ DEPRECATED · Field-Groups family. Older OrkaTaskCard kept here for visual reference only. Do not adopt in new screens — use the Orka Effort-Tier family instead.',
      render: () => (
        <DSDemoFrame caption={`DEPRECATED · Field-Groups · mode="closed"`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {DEMO_TASKS_VARIANTS.map((t) => (
              <OrkaTaskCard key={t.task_id} task={t} mode="closed" defaultOpen={false} fieldGroups onEdit={() => {}} />
            ))}
          </div>
        </DSDemoFrame>
      ),
    },
    'task-card-open': {
      subtitle: '⚠️ DEPRECATED · Field-Groups family. Older OrkaTaskCard with nested editable field groups. Do not adopt in new screens — use the Orka Effort-Tier family instead.',
      render: () => (
        <DSDemoFrame caption={`DEPRECATED · Field-Groups · mode="open" · fieldGroups={true}`}>
          <OrkaTaskCard
            task={DEMO_TASK}
            mode="open"
            fieldGroups
            onStartFocus={() => {}}
            onRecover={() => {}}
            onEdit={() => {}}
          />
        </DSDemoFrame>
      ),
    },
    'task-card-variants': {
      subtitle: '⚠️ DEPRECATED · Field-Groups family. Responsive variants of the older OrkaTaskCard. Kept for visual reference only — do not adopt in new screens.',
      render: () => (
        <>
          <DSDemoFrame caption="DEPRECATED · Field-Groups · stacked (linked-tasks badge)">
            <OrkaTaskCard
              task={{ ...DEMO_TASK, task_linked_count: 3 }}
              mode="stacked"
              fieldGroups
              onEdit={() => {}}
            />
          </DSDemoFrame>
          <div className="h-3" />
          <DSDemoFrame caption="DEPRECATED · Field-Groups · mobile (430 px viewport)">
            <div className="max-w-[430px] mx-auto">
              <OrkaTaskCard task={DEMO_TASK} mode="open" fieldGroups onEdit={() => {}} />
            </div>
          </DSDemoFrame>
        </>
      ),
    },
    'task-field-groups': {
      subtitle: 'Canonical registry — single source of truth consumed by TaskCard + TaskModal + preview',
      render: () => (
        <DSDemoFrame caption="TASK_FIELD_GROUPS — 10 groups, ordered">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {TASK_FIELD_GROUPS.map((g, i) => (
              <div key={g.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border orka-border-subtle orka-surface-raised">
                <div className="min-w-0">
                  <OrkaText size="small" variant="primary" className="font-semibold">{String(i + 1).padStart(2, '0')} · {g.title}</OrkaText>
                  <OrkaText size="micro" variant="tertiary" className="block font-mono normal-case tracking-normal">{g.id}</OrkaText>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] orka-text-tertiary">{g.fields.length} fields</span>
                  {g.defaultOpen && <span className="text-[9px] px-1.5 py-0.5 rounded-full orka-bg-info-soft font-bold uppercase tracking-wider">Open</span>}
                  {g.meta && <span className="text-[9px] px-1.5 py-0.5 rounded-full orka-bg-warning-soft font-bold uppercase tracking-wider">Meta</span>}
                </div>
              </div>
            ))}
          </div>
          <OrkaText size="micro" variant="tertiary" className="block mt-3 normal-case tracking-normal">
            Source: <code className="font-mono text-[11px]">/app/frontend/src/design-system/taskFieldGroups.js</code>
          </OrkaText>
        </DSDemoFrame>
      ),
    },
    'task-row': {
      subtitle: 'Compact list row · IdentityStrip for modal embeds',
      render: () => (
        <>
          <DSDemoFrame caption="OrkaTaskRow — responsible / supporting / observing edge accents">
            <div className="space-y-2">
              <OrkaTaskRow task={{ task_id: 'demo-1', task_title: 'Migrate ScheduledPane shell to OrkaPane', task_priority: 1, task_status: 'In Progress', task_estimated_effort: 5, work_area_name: 'Design System' }} onMove={() => {}} onMenu={() => {}} />
              <OrkaTaskRow task={{ task_id: 'demo-2', task_title: 'Supporting task — orange edge accent', task_priority: 2, task_status: 'To Do', task_estimated_effort: 3, _isSupporting: true }} onMove={() => {}} onMenu={() => {}} />
              <OrkaTaskRow task={{ task_id: 'demo-3', task_title: 'Observing task — teal edge accent', task_priority: 3, task_status: 'Blocked', task_estimated_effort: 8, _isObserved: true }} onMove={() => {}} onMenu={() => {}} />
            </div>
          </DSDemoFrame>
          <div className="h-3" />
          <DSDemoFrame caption="OrkaTaskIdentityStrip — for modal headers">
            <OrkaTaskIdentityStrip task={{ task_title: 'Issue refund for customer #4291', task_priority: 'emergency', task_status: 'inProgress', project_color_hex: '#dc2626', work_area_name: 'Operations' }} />
          </DSDemoFrame>
        </>
      ),
    },

    /* PANES */
    pane: {
      subtitle: 'Phase 6 primitive — drives Backlog and Today–Unplanned panes',
      render: () => (
        <DSDemoFrame caption="Live Backlog + Today-Unplanned panes">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-72">
            <OrkaPane title="Backlog" icon={Inbox} intent="capture" stats={{ effort: 124, filtered: 12, total: 41 }}>
              <div className="space-y-2">
                <OrkaTaskRow task={{ task_id: 'p1', task_title: 'Onboarding tour copy review', task_priority: 2, task_status: 'To Do', task_estimated_effort: 2 }} onMenu={() => {}} />
                <OrkaTaskRow task={{ task_id: 'p2', task_title: 'Audit design tokens for contrast (light)', task_priority: 1, task_status: 'In Progress', task_estimated_effort: 5 }} onMenu={() => {}} />
                <OrkaTaskRow task={{ task_id: 'p3', task_title: 'Visual regression baselines', task_priority: 'medium', task_status: 'Blocked', task_estimated_effort: 3 }} onMenu={() => {}} />
              </div>
            </OrkaPane>
            <OrkaPane title="Today — Unplanned" icon={ListTodo} intent="plan" borderTone="amber" stats={{ effort: 21, duration: '~3h 30m', filtered: 4, total: 4 }}>
              <div className="space-y-2">
                <OrkaTaskRow task={{ task_id: 't1', task_title: 'Reply to procurement', task_priority: 'high', task_status: 'To Do', task_estimated_effort: 1 }} onMenu={() => {}} />
                <OrkaTaskRow task={{ task_id: 't2', task_title: 'Eval YouTube transcript pipeline', task_priority: 'medium', task_status: 'In Progress', task_estimated_effort: 5 }} onMenu={() => {}} />
              </div>
            </OrkaPane>
          </div>
        </DSDemoFrame>
      ),
    },
    'daily-panes-desktop': {
      subtitle: 'Pane-only cockpit — Planner · Today·Unplanned · Backlog. Task cards render as neutral placeholders (governed elsewhere).',
      render: () => (
        <DSDemoFrame caption="Desktop three-pane layout">
          <DailyPanesScope withToolbar>
            <DailyPanesDesktopLayout />
          </DailyPanesScope>
        </DSDemoFrame>
      ),
    },
    'daily-panes-states': {
      subtitle: 'Empty and loading pane states, independent of task-card internals.',
      render: () => (
        <DSDemoFrame caption="Pane states — loading · empty · loading">
          <DailyPanesScope>
            <DailyPanesStates />
          </DailyPanesScope>
        </DSDemoFrame>
      ),
    },
    'daily-panes-mobile': {
      subtitle: 'Responsive stacked panes — each pane collapses independently.',
      render: () => (
        <DSDemoFrame caption="Mobile stacked & collapsible">
          <DailyPanesScope>
            <DailyPanesMobileLayout />
          </DailyPanesScope>
        </DSDemoFrame>
      ),
    },
    'daily-panes-implementation': {
      subtitle: 'Target API and hard rules for the DailyDashboardPane contract.',
      render: () => (
        <DSDemoFrame caption="Implementation contract">
          <DailyPanesScope>
            <DailyPanesImplementation />
          </DailyPanesScope>
        </DSDemoFrame>
      ),
    },

    /* DATE PICKER — unified, one sub-section per demo (no bespoke chrome) */
    'date-picker-calendar': {
      subtitle: 'V4 visible-grid calendar — every visible cell is a normal data-aware scheduling cell',
      render: () => <DatePickerCalendarDemo />,
    },
    'date-picker-triggers': {
      subtitle: 'Selected · empty · ghost · error · locked',
      render: () => (
        <DSDemoFrame caption="Trigger button variants">
          <DatePickerTriggerVariants />
        </DSDemoFrame>
      ),
    },
    'date-picker-cells': {
      subtitle: 'Empty · loading · weekend · today · selected · missed · overloaded · stale',
      render: () => (
        <DSDemoFrame caption="Day-cell state matrix">
          <DatePickerCellMatrix />
        </DSDemoFrame>
      ),
    },
    'date-picker-grids': {
      subtitle: '7 × 4 / 7 × 5 / 7 × 6 — every visible cell behaves the same',
      render: () => (
        <DSDemoFrame caption="Grid sizes">
          <DatePickerGridSizes mode="loaded" />
        </DSDemoFrame>
      ),
    },
    'date-picker-legend': {
      subtitle: 'Canonical effort thresholds (mirrored from Foundations · Effort scale)',
      render: () => (
        <DSDemoFrame caption="Effort legend">
          <DatePickerEffortLegend />
        </DSDemoFrame>
      ),
    },
    'date-picker-decision': {
      subtitle: 'Optional warning when the selected day is already Overloaded or Doom',
      render: () => (
        <DSDemoFrame caption="Overload decision bar">
          <DatePickerDecisionBar />
        </DSDemoFrame>
      ),
    },
    'app-shell-variants': {
      subtitle: 'Whole-app shell — expanded / collapsed sidebar, with optional right drawer',
      render: () => <AppShellVariantsShowcase />,
    },
    'app-shell-anatomy': {
      subtitle: 'Left panel regions: logo + Orka AI · The Deck · modules · appearance · profile · collapse',
      render: () => <LeftPanelAnatomyShowcase />,
    },
    'app-layouts': {
      subtitle: 'Five canonical App Page variants — desktop + mobile previews',
      render: () => <AppLayoutsShowcase />,
    },
    'color-system': {
      subtitle: 'Every color currently registered — swatches + raw code (copy-paste ready)',
      render: () => <ColorSystemShowcase />,
    },
  };
}
